// Import Dependencies
import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { withRouter, Redirect, Link } from 'react-router-dom';
import { ghArea } from '~/common/globals';

// Import Components
import Centering from '~/common/components/Centering';
import BackButton from '~/common/components/BackButton';
import NumberSelection from '~/common/components/NumberSelection';
import Input from '~/common/components/Input';
import LoadingSpinner from '~/common/components/LoadingSpinner';
import NewBatch from '~/common/components/NewBatch';
import SelectedNumberButton from '~/common/components/SelectedNumberButton';
import SelectNumberButton from '~/common/components/SelectNumberButton';
import SearchIconButton from '~/common/components/SearchIconButton';
import Stepper from '~/common/components/Stepper';
import ClearIcon from '~/common/components/ClearSearchButton';
import HeaderGroup from '~/common/components/HeaderGroup';

import { scBeacon, createPersonalizedPurchaseEvent, gtm, clickCtaEvent, viewContentEvent } from '~/common/utils/tracking.js';
import { isAreaCodePremium } from '~/common/utils/premiumAreaCodes.js';
import { extractAreaCode } from '~/common/utils/numberOperations.js';
import {
    FLOW_STEP_NUMBERS,
    PATH_PLANS, PATH_TRIAL_SIGNUP
} from '~/common/utils/consts.js';

// Import Stylesheet
import styles from './local-search.css';

@inject('lockNumberStore', 'modalStore', 'localNumbersStore', 'progressStore', 'trialSignupStore')
@observer
class LocalSearch extends Component {
    componentDidMount() {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        const { localNumbersStore, progressStore, trialSignupStore } = this.props;

        // checks if this is the app entry point and sets corresponding flag
        if (!progressStore.firstView) {
            progressStore.setFirstView(FLOW_STEP_NUMBERS);
        }

        const { isTrialFlow } = trialSignupStore;
        gtm(viewContentEvent(isTrialFlow ? 'trial flow' : 'buy flow', 'numbers', 'local number'));


        localNumbersStore.setLocalNumberError(false);
        localNumbersStore.updateSearch('');
        localNumbersStore.resetSelectedAreaCode();

        if (trialSignupStore.isTrialFlow && this.isSelectedAreaCodePremium()) {
            localNumbersStore.resetLocalNumbers();
        }

        if (ghArea()) {
            localNumbersStore.setInitialSearch(true);
            localNumbersStore.updateSearch(ghArea());
            localNumbersStore.getLocalNumbersByNpa(ghArea());
        }

        // remove flag of auto redirect once the user has seen this page once
        if (localNumbersStore.preselectedLocal) localNumbersStore.setPreselectedLocal(false);
    }

    // needed for when localNumbers.selectedNumber is empty (this.selectedNumber() could still be full)
    isSelectedAreaCodePremium() {
        if (this.props.localNumbersStore.localNumbers.length > 0) {
            return isAreaCodePremium(extractAreaCode(this.selectedNumber()));
        }
        return false;
    }

    selectedNumber() {
        const selectedNumber = this.props.localNumbersStore.selectedNumber;
        if (selectedNumber && selectedNumber.UI) {
            return this.props.localNumbersStore.selectedNumber;
        }
        if (this.props.localNumbersStore.localNumbers.length > 0) {
            this.props.localNumbersStore.setPreSelectedNumberIndex(0);
            return this.props.localNumbersStore.selectedNumber;
        }
        return {
            UI: 'Loading...'
        };
    }

    selectNumber(i) {
        const { localNumbersStore, lockNumberStore, modalStore, trialSignupStore } = this.props;
        // change the index if one is passed
        if (i) {
            localNumbersStore.setPreSelectedNumberIndex(i);
        }
        // set selected number in store
        lockNumberStore.setSelectedNumber(localNumbersStore.selectedNumber);
        // set selected number type to local
        lockNumberStore.setSelectedNumberType('Local');

        if (trialSignupStore.isTrialFlow && lockNumberStore.isSelectedAreaCodePremium) {
            modalStore.setModalOptions({
                childComponentString: 'upgradeModal',
                isModalClosable: true,
                modalType: 'xlarge',
                isModalOpen: true
            });
        } else {
            lockNumberStore.lockNumberToSession();

            // track SCBeacon: selected Local number searched for by user
            scBeacon(createPersonalizedPurchaseEvent('1'));
        }
    }

    setSelectedAreaCode(areaCode) {
        this.props.localNumbersStore.setSelectedAreaCode(areaCode);
    }

    clearSearch() {
        this.props.localNumbersStore.setLocalNumberError(false);
        this.props.localNumbersStore.updateSearch('');
    }

    updateSearch(e) {
        this.props.localNumbersStore.setLocalNumberError(false);
        this.props.localNumbersStore.updateSearch(e.target.value);
        this.props.localNumbersStore.resetSelectedAreaCode();
        this.props.localNumbersStore.setSelectedAreaCode(e.target.value);
    }

    getLocalNumbersByNpa(e) {
        e.preventDefault();
        this.props.localNumbersStore.setLocalNumberError(false);

        const areaCode = this.props.localNumbersStore.selectedAreaCode;
        const regex = new RegExp('^[0-9]{3}$');
        if (areaCode.length !== 3) {
            this.props.localNumbersStore.setLocalNumberError(true);
            return false;
        }
        if (typeof areaCode === 'string' && regex.test(parseInt(areaCode, 10))) {
            // user has searched
            this.props.localNumbersStore.setInitialSearch(true);
            // fetch a new batch of local numbers
            this.props.localNumbersStore.getLocalNumbersByNpa(areaCode);
        }
        return true;
    }

    renderHint() {
        const { localNumbersStore, trialSignupStore } = this.props;
        const { localNumbers, localNumberError, initialSearch, query, previousAreaCode, localNumbersFetching } = localNumbersStore;
        // error getting numbers or no numbers
        if (localNumberError) {
            if (query.length !== 3) {
                return <p className={styles.error}>Area codes must be 3 digits long.</p>;
            }
            return <p className={styles.error}>Rats! All the phone numbers with area code {previousAreaCode} are already taken. Try changing your search.</p>;
        }

        // if fetching numbers dont display anything
        if (localNumbersFetching) {
            return <p className={styles.recommend}>Loading...</p>;
        }

        if (trialSignupStore.isTrialFlow && this.isSelectedAreaCodePremium()) {
            return (
                <div>
                    <p className={styles.premium}>Area code {extractAreaCode(this.selectedNumber())} is designated as a premium area code, and is not available for use in the trial version of Grasshopper.</p>
                    <p className={styles.premium}>
                        Still want a number with a premium area code? No problem!<br />
                        Just
                        <Link
                            onClick={() => {
                                trialSignupStore.setIsTrialFlow(false);
                                gtm(clickCtaEvent('purchase the full version of Grasshopper', PATH_PLANS, 'local number search'));
                            }}
                            to={PATH_PLANS}
                        > purchase the full version of Grasshopper. &#187;</Link>
                    </p>
                </div>
            );
        }

        // user has searched and there are numbers
        if (initialSearch && localNumbers.length > 0) {
            return (
                <div>
                    <p className={styles.recommend}>We recommend...</p>
                    <SelectedNumberButton number={this.selectedNumber()} onClick={() => this.selectNumber()} />
                </div>
            );
        }

        // default view
        return <p className={styles.preferred}>Enter your preferred 3-digit area code to generate a list of available phone numbers.</p>;
    }

    renderNumbersArea() {
        const { localNumbersStore, lockNumberStore, trialSignupStore } = this.props;
        const { localNumbers, localNumbersFetching, initialSearch } = localNumbersStore;
        const { numberIsLocked, selectedNumber } = lockNumberStore;

        // user has searched and there are numbers
        if (
            initialSearch &&
            localNumbers.length > 0 &&
            !(trialSignupStore.isTrialFlow &&
                this.isSelectedAreaCodePremium() &&
                !this.selectedNumber)
        ) {
            return (
                <NumberSelection>
                    {localNumbersFetching ?
                        <div className={styles['loading-box']}>
                            {!(trialSignupStore.isTrialFlow && this.isSelectedAreaCodePremium()) &&
                                <p className={styles['great-numbers']}>
                                    Or select from these great numbers:
                                </p>
                            }
                            <div className={styles.spinner}>
                                <LoadingSpinner />
                            </div>
                        </div>
                        :
                        <div>
                            {!(trialSignupStore.isTrialFlow && this.isSelectedAreaCodePremium()) &&
                                <p className={styles['great-numbers']}>
                                    Or select from these great numbers:
                                </p>
                            }
                            <div className={styles['number-selection']}>
                                {localNumbers.map((number, i) => {
                                    if (selectedNumber
                                        && selectedNumber.E164
                                        && localNumbers[i].E164 === selectedNumber.E164
                                        && numberIsLocked) {
                                        return (
                                            <SelectNumberButton
                                                key={number.E164}
                                                number={number}
                                                onClick={() => this.selectNumber(i)}
                                                selected={true}
                                                value={i}
                                            >
                                                {number.UI}
                                            </SelectNumberButton>
                                        );
                                    }
                                    return (
                                        <SelectNumberButton
                                            key={number.E164}
                                            number={number}
                                            onClick={() => this.selectNumber(i)}
                                            value={i}
                                        >
                                            {number.UI}
                                        </SelectNumberButton>
                                    );
                                })}
                            </div>
                            <div className={styles['get-new-batch']}>
                                <NewBatch fetching={localNumbersFetching} onClick={e => this.getLocalNumbersByNpa(e)} />
                            </div>
                        </div>
                    }
                </NumberSelection>
            );
        }

        return null;
    }

    render() {
        const { localNumbersStore, lockNumberStore, progressStore, trialSignupStore } = this.props;
        const { query } = localNumbersStore;
        const { numberIsLocked, lockingNumber } = lockNumberStore;

        // if number is successfully locked
        if (numberIsLocked && lockingNumber) {
            // redirect to the trial page if in trial mode
            const redirectView = trialSignupStore.isTrialFlow ? PATH_TRIAL_SIGNUP : progressStore.nextStepFromNumbers;
            // redirect to billing page or plans page if plan is not selected
            return (
                <Redirect push={true} to={redirectView} />
            );
        }

        return (
            <div className={styles.wrapper}>
                <Centering lessBottomMargin relative={true}>
                    <Stepper currentStep={FLOW_STEP_NUMBERS} />
                    <BackButton currentStep={FLOW_STEP_NUMBERS} />
                    <HeaderGroup>
                        <h1>Which local area code are you <br /> looking for?</h1>
                    </HeaderGroup>
                    <form className={styles['local-search-form']} onSubmit={e => this.getLocalNumbersByNpa(e)}>
                        <Input
                            autoComplete="off"
                            defaultValue={query}
                            id="local__input"
                            maxLength={3}
                            name="localSearchInput"
                            onChange={e => this.updateSearch(e)}
                            placeholder="— — —"
                            restrictCharacters="numericOnly"
                            type="tel"
                        />
                        {!!query.length && <ClearIcon onClick={() => this.clearSearch()} />}
                        <SearchIconButton onClick={e => this.getLocalNumbersByNpa(e)}></SearchIconButton>
                    </form>
                    {this.renderHint()}
                </Centering>
                {this.renderNumbersArea()}
            </div>
        );
    }
}

export default withRouter(LocalSearch);
